"use client";

import { useFormatDate } from "@/lib/i18n/format";
import { useSmoothText } from "@convex-dev/agent/react";
import type { Id } from "@v1/backend/convex/_generated/dataModel";
import { cn } from "@v1/ui/utils";
import { BookOpen, Check, Loader2, X } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactElement, ReactNode } from "react";
import { MessengerAgentAvatar, MessengerPersonAvatar } from "./messenger-avatars";
import { MessengerAttachmentChips } from "./messenger-attachments";
import { useMessenger } from "./messenger-context";
import { MessengerMarkdown } from "./messenger-markdown";
import { splitSourceLinks } from "./messenger-sources";
import type { MessengerToolStep } from "./messenger-tool-steps";

const GROUP_WINDOW_MS = 5 * 60 * 1000;

export interface MessengerBubbleItem {
  readonly id: string;
  readonly author: "self" | "agent" | "staff" | "contact";
  /** Null = the app's own word (Mitarbeiter, Kontakt). */
  readonly name: string | null;
  readonly text: string;
  /** Null while the answer is still streaming. */
  readonly at: number | null;
  readonly commentId?: Id<"taskComments">;
  readonly attachments?: ReadonlyArray<{ readonly file_id: Id<"files">; readonly name: string }>;
  /** Agent only: tools it ran for this answer, so the customer sees it working. */
  readonly tools?: readonly MessengerToolStep[];
  /** Agent answer still arriving: its text types out. */
  readonly streaming?: boolean;
}

const STREAM_CHARS_PER_SEC = 400;

function sameGroup(a: MessengerBubbleItem, b: MessengerBubbleItem): boolean {
  if (a.author !== b.author || a.name !== b.name) return false;
  if (a.at === null || b.at === null) return true;
  return Math.abs(b.at - a.at) < GROUP_WINDOW_MS;
}

function useSourceChips(text: string): { body: string; chips: ReactNode } {
  const { config, nav } = useMessenger();
  const { body, sources } = splitSourceLinks(text, config.help_org_slug);
  const chips =
    sources.length === 0 ? null : (
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {sources.map((source) => (
          <button
            key={source.pageId}
            type="button"
            onClick={() => nav.push({ kind: "article", pageId: source.pageId })}
            className="inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-full bg-action-500/[0.08] px-3 text-[12px] font-medium text-primary/70 transition-colors hover:bg-action-500/[0.12]"
          >
            <BookOpen className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
            <span className="min-w-0 truncate">{source.title}</span>
          </button>
        ))}
      </div>
    );
  return { body, chips };
}

function useToolLabel(): (step: MessengerToolStep) => string {
  const t = useTranslations("messenger.toolActivity");
  return ({ tool, state }) => {
    switch (tool) {
      case "searchPages":
        return t("searchPages", { state });
      case "pageRead":
        return t("pageRead", { state });
      case "pageGrep":
        return t("pageGrep", { state });
      case "createTaskWithComment":
        return t("createTaskWithComment", { state });
      case "convertChatToTicket":
        return t("convertChatToTicket", { state });
      case "createComment":
        return t("createComment", { state });
      case "getContactById":
        return t("getContactById", { state });
      case "searchTasks":
        return t("searchTasks", { state });
      case "searchFiles":
        return t("searchFiles", { state });
      case "getFileById":
        return t("getFileById", { state });
      case "searchProperties":
        return t("searchProperties", { state });
      case "searchUnits":
        return t("searchUnits", { state });
      case "listUnits":
        return t("listUnits", { state });
      default:
        return t("sonstiges", { state });
    }
  };
}

function ToolStateIcon({ state }: { readonly state: MessengerToolStep["state"] }): ReactElement {
  switch (state) {
    case "running":
      return <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />;
    case "done":
      return <Check className="size-3.5 shrink-0" strokeWidth={2} aria-hidden />;
    case "failed":
      return <X className="size-3.5 shrink-0" strokeWidth={2} aria-hidden />;
  }
}

function ToolSteps({ steps }: { readonly steps: readonly MessengerToolStep[] }): ReactElement {
  const label = useToolLabel();
  return (
    <ul className="flex min-w-0 flex-col gap-1">
      {steps.map((step) => (
        <li key={step.id} className="flex min-w-0 items-center gap-1.5 text-[12px] text-primary/55">
          <ToolStateIcon state={step.state} />
          <span className="min-w-0 truncate">{label(step)}</span>
        </li>
      ))}
    </ul>
  );
}

function AgentBody({
  text,
  tools,
  streaming,
}: {
  readonly text: string;
  readonly tools: readonly MessengerToolStep[];
  readonly streaming: boolean;
}): ReactElement {
  const { body, chips } = useSourceChips(text);
  // Models often send the answer in one burst; it still types out instead of popping in.
  const [visible, { isStreaming }] = useSmoothText(body, {
    charsPerSec: STREAM_CHARS_PER_SEC,
    startStreaming: streaming,
  });
  return (
    <>
      {tools.length > 0 ? (
        <div className={cn(visible.length > 0 && "mb-1.5")}>
          <ToolSteps steps={tools} />
        </div>
      ) : null}
      {visible.length > 0 ? <MessengerMarkdown text={visible} /> : null}
      {isStreaming ? null : chips}
    </>
  );
}

/** Thread bubbles: own messages right in green, others left with avatar. */
export function MessengerBubbles({
  items,
  taskId,
  footer,
}: {
  readonly items: readonly MessengerBubbleItem[];
  readonly taskId?: Id<"tasks">;
  readonly footer?: ReactNode;
}): ReactElement {
  const t = useTranslations("messenger.thread");
  const formatDate = useFormatDate();
  return (
    <ul className="flex min-w-0 flex-col px-4 py-4">
      {items.map((item, idx) => {
        const prev = items[idx - 1];
        const next = items[idx + 1];
        const continuesPrev = prev !== undefined && sameGroup(prev, item);
        const continuesNext = next !== undefined && sameGroup(item, next);
        const isSelf = item.author === "self";
        // The AI needs no name over its bubbles; the triangle says it. People keep theirs.
        const name =
          item.author === "agent"
            ? null
            : (item.name ?? (item.author === "staff" ? t("mitarbeiter") : t("kontakt")));
        return (
          <li
            key={item.id}
            className={cn(
              "flex min-w-0 items-end gap-2",
              isSelf ? "justify-end" : "justify-start",
              continuesPrev ? "mt-1" : "mt-4 first:mt-0",
            )}
          >
            {!isSelf ? (
              <span className={cn("shrink-0", continuesNext && "invisible")}>
                {item.author === "agent" || name === null ? (
                  <MessengerAgentAvatar size="sm" />
                ) : (
                  <MessengerPersonAvatar name={name} />
                )}
              </span>
            ) : null}
            <div className={cn("flex min-w-0 max-w-[80%] flex-col gap-1", isSelf ? "items-end" : "items-start")}>
              {!isSelf && !continuesPrev && name !== null ? (
                <span className="px-1 text-[11px] font-medium leading-tight text-primary/50">{name}</span>
              ) : null}
              <div
                className={cn(
                  "min-w-0 max-w-full rounded-2xl px-3.5 py-2 text-[14px] leading-relaxed shadow-[0_1px_2px_rgba(0,0,0,0.03)] [overflow-wrap:anywhere]",
                  isSelf ? "bg-action-500 text-white" : "bg-action-500/[0.05] text-primary/90",
                  !continuesNext && (isSelf ? "rounded-br-md" : "rounded-bl-md"),
                )}
              >
                {item.author === "agent" ? (
                  <AgentBody text={item.text} tools={item.tools ?? []} streaming={item.streaming === true} />
                ) : (
                  <p className="whitespace-pre-wrap break-words">{item.text}</p>
                )}
                {item.attachments && item.attachments.length > 0 && taskId && item.commentId ? (
                  <MessengerAttachmentChips
                    taskId={taskId}
                    commentId={item.commentId}
                    attachments={item.attachments}
                    isSelf={isSelf}
                  />
                ) : null}
              </div>
              {!continuesNext && item.at !== null ? (
                <span className="px-1 text-[10.5px] leading-tight tabular-nums text-primary/35">
                  {formatDate.time(item.at)}
                </span>
              ) : null}
            </div>
          </li>
        );
      })}
      {footer}
    </ul>
  );
}
